import {
  AfterViewInit,
  Component,
  ElementRef,
  OnDestroy,
  ViewChild,
} from "@angular/core";

import {
  CookielessCallback,
  GenerateTokensCallback,
  LookerEmbedCookielessSessionData,
  LookerEmbedCookielessTokenData,
  getEmbedSDK,
} from "@looker/embed-sdk";
import { LookerService } from "../../../services/looker.service";
import { ProgressBarHelper } from "../../../services/progress-bar-helper.service";
import { ActivatedRoute, Data } from "@angular/router";
import { Subscription, firstValueFrom } from "rxjs";
import { CurityAuthenticationService } from "../../../services/curity-authentication.service";
import { WindowRefService } from "src/app/services/window-ref.service";

const SESSION_TOKEN_KEY: string = "LookerSessionToken";

@Component({
  selector: "app-dashboard",
  standalone: true,
  imports: [],
  templateUrl: "./dashboard.component.html",
  styleUrls: ["./dashboard.component.scss"],
})
export class DashboardComponent implements AfterViewInit, OnDestroy {
  private activeRouteSub: Subscription = null;
  private lookerHostSub: Subscription = null;

  pageError: boolean = false;

  @ViewChild("embed-container") embedContainer: ElementRef;

  constructor(
    private lookerService: LookerService,
    private progressBarHelper: ProgressBarHelper,
    private activeRoute: ActivatedRoute,
  ) {
    localStorage.removeItem(SESSION_TOKEN_KEY);
  }

  ngAfterViewInit(): void {
    this.activeRouteSub = this.activeRoute.data.subscribe((d: Data): void => {
      let dashboardId = this.getDashboardId(d);
      this.lookerHostSub = this.lookerService
        .getLookerHost(d.apiVersion)
        .subscribe(
          (resp): void => {
            this.initEmbedContainer(resp.lookerHostname, d.apiVersion);
            this.loadDashboard(dashboardId);
          },
          (): void => {
            this.setErrorState();
          },
        );
    });
  }

  ngOnDestroy(): void {
    this.activeRouteSub.unsubscribe();
    this.lookerHostSub.unsubscribe();
  }

  setErrorState(): void {
    this.pageError = true;
    this.hideProgressBar();
  }

  hideProgressBar(): void {
    this.progressBarHelper.setVisible(false);
  }

  initEmbedContainer(lookerHostname: string, apiVersion: string): void {
    getEmbedSDK().initCookieless(
      lookerHostname,
      this.getAcquireEmbedSessionCallback(apiVersion),
      this.getGenerateEmbedTokensCallback(apiVersion),
    );
  }

  async loadDashboard(dashboardId: string): Promise<void> {
    try {
      let connection = await getEmbedSDK()
        .createDashboardWithId(dashboardId)
        .appendTo("#embed-container")
        .on("dashboard:run:complete", () => this.hideProgressBar())
        .build()
        .connect();
      connection.asDashboardConnection().run();
    } catch (error) {
      console.error("Looker Dashboard connection error:", error);
      this.setErrorState();
    }
  }

  getAcquireEmbedSessionCallback(apiVersion: string): CookielessCallback {
    return (): Promise<LookerEmbedCookielessSessionData> => {
      return this.acquireEmbedSessionCallback(apiVersion);
    };
  }

  getGenerateEmbedTokensCallback(apiVersion: string): GenerateTokensCallback {
    return (
      tokens: LookerEmbedCookielessTokenData,
    ): Promise<LookerEmbedCookielessSessionData> => {
      return this.generateEmbedTokensCallback(apiVersion, tokens);
    };
  }

  async acquireEmbedSessionCallback(
    apiVersion: string,
  ): Promise<LookerEmbedCookielessSessionData> {
    const resp = await firstValueFrom(
      this.lookerService.acquireEmbedSession(apiVersion),
    );
    if (!resp) {
      console.error("acquire-embed-session failed", { resp });
      this.setErrorState();
      throw new Error("acquire-embed-session failed");
    }
    localStorage.setItem(SESSION_TOKEN_KEY, resp.session_reference_token);
    return resp as LookerEmbedCookielessSessionData;
  }

  async generateEmbedTokensCallback(
    apiVersion: string,
    { api_token, navigation_token }: LookerEmbedCookielessTokenData,
  ): Promise<LookerEmbedCookielessSessionData> {
    if (!localStorage.getItem(SESSION_TOKEN_KEY)) {
      return {
        session_reference_token_ttl: 0,
      } as LookerEmbedCookielessSessionData;
    }
    const body = JSON.stringify({ api_token, navigation_token });
    const resp = await firstValueFrom(
      this.lookerService.generateEmbedTokens(apiVersion, body),
    );
    if (!resp) {
      console.error("generate-embed-tokens failed", { resp });
      this.setErrorState();
      throw new Error("generate-embed-tokens failed");
    }
    if (
      resp.session_reference_token &&
      resp.session_reference_token.length > 0
    ) {
      localStorage.setItem(SESSION_TOKEN_KEY, resp.session_reference_token);
    }
    return resp as LookerEmbedCookielessSessionData;
  }

  private getDashboardId(d: Data): string {
    if (d.apiVersion === "V2") {
      return d.dashboardId;
    } else {
      let authService: CurityAuthenticationService =
        new CurityAuthenticationService(new WindowRefService());
      if (authService.isEnterprise() && d.enterpriseDashboardId) {
        return d.enterpriseDashboardId;
      }
      return d.dashboardId;
    }
  }
}
